import type {
  IDailyHourlyReadingsParams,
  IDailyTemperaturePeriodFilter,
  IDailyTemperatureSensor,
  IDailyTemperatureTableSensor,
} from '@/interfaces/entities/daily-temperature'
import { useSensorStore } from '@/store/sensor'
import {
  buildReadingsQueryParams,
  createDefaultPeriodFilter,
  formatShortDisplayDate,
  isCompareSearchActive,
  todayISO,
  validateSearchFilters,
} from '@/utils/daily-temperature/period-filter'
import { useErrorStore } from '@/store/error'
import { computed, ref } from 'vue'
import { useI18n } from '@/composable/useI18n'

function mergeSensors(
  existing: IDailyTemperatureSensor[],
  incoming: IDailyTemperatureSensor[],
): IDailyTemperatureSensor[] {
  const byId = new Map(existing.map((sensor) => [sensor.id, sensor]))
  for (const sensor of incoming) {
    byId.set(sensor.id, sensor)
  }
  return [...byId.values()]
}

function buildSensorMaps(
  primarySensors: IDailyTemperatureSensor[],
  compareSensors: IDailyTemperatureSensor[],
  isCompareEnabled: boolean,
) {
  const primarySensorById: Record<number, IDailyTemperatureSensor> = {}
  const compareSensorById: Record<number, IDailyTemperatureSensor> = {}
  const sensorMeta = new Map<number, IDailyTemperatureTableSensor>()

  for (const sensor of primarySensors) {
    primarySensorById[sensor.id] = sensor
    sensorMeta.set(sensor.id, {
      id: sensor.id,
      name: sensor.name,
      setpoint: sensor.setpoint,
    })
  }

  if (isCompareEnabled) {
    for (const sensor of compareSensors) {
      compareSensorById[sensor.id] = sensor
      if (!sensorMeta.has(sensor.id)) {
        sensorMeta.set(sensor.id, {
          id: sensor.id,
          name: sensor.name,
          setpoint: sensor.setpoint,
        })
      }
    }
  }

  const tableSensors = [...sensorMeta.values()].sort((a, b) =>
    a.name.localeCompare(b.name, 'pt-BR'),
  )

  return { tableSensors, primarySensorById, compareSensorById }
}

export function useDailyTemperatureSearch() {
  const { t } = useI18n()
  const sensorStore = useSensorStore()

  const draftPrimaryDate = ref(todayISO())
  const draftCompareDate = ref('')
  const draftIsCompareEnabled = ref(false)
  const draftPeriodFilter = ref<IDailyTemperaturePeriodFilter>(createDefaultPeriodFilter())
  const draftSensorIds = ref<number[]>([])

  const appliedPrimaryDate = ref('')
  const appliedCompareDate = ref('')
  const appliedIsCompareEnabled = ref(false)
  const appliedPeriodFilter = ref<IDailyTemperaturePeriodFilter>(createDefaultPeriodFilter())
  const appliedSensorIds = ref<number[]>([])

  const accumulatedPrimarySensors = ref<IDailyTemperatureSensor[]>([])
  const accumulatedCompareSensors = ref<IDailyTemperatureSensor[]>([])
  const hours = ref<string[]>([])
  const hasSearched = ref(false)
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const appliedPeriodFilterLabel = ref('')
  const currentPage = ref(1)
  const hasMore = ref(false)
  const totalSensors = ref(0)

  const appliedQueryParams = ref<IDailyHourlyReadingsParams | null>(null)

  const gridData = computed(() =>
    buildSensorMaps(
      accumulatedPrimarySensors.value,
      accumulatedCompareSensors.value,
      appliedIsCompareEnabled.value,
    ),
  )

  const tableSensors = computed(() => gridData.value.tableSensors)
  const primarySensorById = computed(() => gridData.value.primarySensorById)
  const compareSensorById = computed(() => gridData.value.compareSensorById)

  const primaryDateShort = computed(() =>
    appliedPrimaryDate.value ? formatShortDisplayDate(appliedPrimaryDate.value) : '',
  )

  const compareDateShort = computed(() =>
    appliedCompareDate.value ? formatShortDisplayDate(appliedCompareDate.value) : '',
  )

  const isCompareGridActive = computed(
    () => appliedIsCompareEnabled.value && accumulatedCompareSensors.value.length > 0,
  )

  const hasGridData = computed(() => hasSearched.value && tableSensors.value.length > 0)

  const isEmptyResult = computed(
    () => hasSearched.value && !isLoading.value && tableSensors.value.length === 0,
  )

  const loadedSensorsCount = computed(() => accumulatedPrimarySensors.value.length)

  const remainingSensors = computed(() =>
    Math.max(0, totalSensors.value - loadedSensorsCount.value),
  )

  function applyPrimaryResponse(
    response: Awaited<ReturnType<typeof sensorStore.fetchDailyTemperature>>,
    append: boolean,
  ) {
    hours.value = response.hours
    appliedPeriodFilterLabel.value = response.period_filter?.label ?? ''
    totalSensors.value = response.pagy?.count ?? response.sensors.length
    currentPage.value = response.pagy?.page ?? currentPage.value
    hasMore.value = response.pagy?.next != null

    if (append) {
      accumulatedPrimarySensors.value = mergeSensors(
        accumulatedPrimarySensors.value,
        response.sensors,
      )
      return
    }

    accumulatedPrimarySensors.value = response.sensors
  }

  function applyCompareResponse(
    response: Awaited<ReturnType<typeof sensorStore.fetchDailyTemperature>>,
    append: boolean,
  ) {
    if (append) {
      accumulatedCompareSensors.value = mergeSensors(
        accumulatedCompareSensors.value,
        response.sensors,
      )
      return
    }

    accumulatedCompareSensors.value = response.sensors
  }

  function clearFilters() {
    draftPrimaryDate.value = todayISO()
    draftCompareDate.value = ''
    draftIsCompareEnabled.value = false
    draftPeriodFilter.value = createDefaultPeriodFilter()
    draftSensorIds.value = []
  }

  async function search(): Promise<boolean> {
    const validationError = validateSearchFilters(
      draftPrimaryDate.value,
      draftCompareDate.value,
      draftIsCompareEnabled.value,
      draftPeriodFilter.value,
    )

    if (validationError) {
      useErrorStore().setErrors([t(`daily_temperature.${validationError}`)])
      return false
    }

    isLoading.value = true
    appliedPeriodFilterLabel.value = ''

    const periodFilter = { ...draftPeriodFilter.value }
    const sensorIds = [...draftSensorIds.value]
    const primaryParams = buildReadingsQueryParams(draftPrimaryDate.value, periodFilter, {
      page: 1,
      sensorIds,
    })
    const shouldCompare = isCompareSearchActive(
      draftIsCompareEnabled.value,
      draftCompareDate.value,
    )

    sensorStore.invalidateDailyTemperatureCache(primaryParams)

    try {
      if (shouldCompare) {
        const compareParams = buildReadingsQueryParams(
          draftCompareDate.value.trim(),
          periodFilter,
          { page: 1, sensorIds },
        )
        sensorStore.invalidateDailyTemperatureCache(compareParams)

        const [primary, compare] = await Promise.all([
          sensorStore.fetchDailyTemperature(primaryParams, true),
          sensorStore.fetchDailyTemperature(compareParams, true),
        ])

        accumulatedCompareSensors.value = []
        applyPrimaryResponse(primary, false)
        applyCompareResponse(compare, false)
      } else {
        accumulatedCompareSensors.value = []
        const primary = await sensorStore.fetchDailyTemperature(primaryParams, true)
        applyPrimaryResponse(primary, false)
      }

      appliedPrimaryDate.value = draftPrimaryDate.value
      appliedCompareDate.value = shouldCompare ? draftCompareDate.value.trim() : ''
      appliedIsCompareEnabled.value = shouldCompare
      appliedPeriodFilter.value = periodFilter
      appliedSensorIds.value = sensorIds
      appliedQueryParams.value = primaryParams
      hasSearched.value = true
      return true
    } catch {
      accumulatedPrimarySensors.value = []
      accumulatedCompareSensors.value = []
      hours.value = []
      appliedPeriodFilterLabel.value = ''
      hasMore.value = false
      totalSensors.value = 0
      hasSearched.value = false
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function loadMore(): Promise<boolean> {
    if (!appliedQueryParams.value || !hasMore.value || isLoadingMore.value) {
      return false
    }

    isLoadingMore.value = true

    const nextPage = currentPage.value + 1
    const primaryParams = buildReadingsQueryParams(
      appliedPrimaryDate.value,
      appliedPeriodFilter.value,
      {
        page: nextPage,
        items: appliedQueryParams.value.items,
        sensorIds: appliedSensorIds.value,
      },
    )

    try {
      if (appliedIsCompareEnabled.value && appliedCompareDate.value) {
        const compareParams = buildReadingsQueryParams(
          appliedCompareDate.value,
          appliedPeriodFilter.value,
          {
            page: nextPage,
            items: appliedQueryParams.value.items,
            sensorIds: appliedSensorIds.value,
          },
        )

        const [primary, compare] = await Promise.all([
          sensorStore.fetchDailyTemperature(primaryParams),
          sensorStore.fetchDailyTemperature(compareParams),
        ])

        applyPrimaryResponse(primary, true)
        applyCompareResponse(compare, true)
      } else {
        const primary = await sensorStore.fetchDailyTemperature(primaryParams)
        applyPrimaryResponse(primary, true)
      }

      return true
    } catch {
      useErrorStore().setErrors([t('daily_temperature.errors.load_more_failed')])
      return false
    } finally {
      isLoadingMore.value = false
    }
  }

  return {
    draftPrimaryDate,
    draftCompareDate,
    draftIsCompareEnabled,
    draftPeriodFilter,
    draftSensorIds,
    appliedPrimaryDate,
    appliedCompareDate,
    appliedIsCompareEnabled,
    appliedSensorIds,
    isCompareGridActive,
    hasSearched,
    isLoading,
    isLoadingMore,
    hours,
    appliedPeriodFilterLabel,
    tableSensors,
    primarySensorById,
    compareSensorById,
    primaryDateShort,
    compareDateShort,
    hasGridData,
    isEmptyResult,
    hasMore,
    totalSensors,
    loadedSensorsCount,
    remainingSensors,
    clearFilters,
    search,
    loadMore,
    maxDate: todayISO(),
  }
}
